import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The Meta OAuth flow returns access_token + state in the URL fragment (#).
// This page extracts them and POSTs to /api/bloom/oauth/finish, then routes
// the advertiser back to their dashboard's Bloom tab.
export default function BloomCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('working'); // working | ok | error
  const [message, setMessage] = useState('Finishing the Instagram connection…');

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const search = new URLSearchParams(window.location.search);
    const access_token = hash.get('access_token');
    const state = hash.get('state') || search.get('state');
    const err = search.get('error') || hash.get('error') || search.get('error_reason');

    if (err) {
      setStatus('error');
      setMessage(`Instagram declined the connection: ${err}. You can try again from the Bloom tab.`);
      return;
    }
    if (!access_token || !state) {
      setStatus('error');
      setMessage('Missing OAuth token or state. Please retry from the Bloom tab.');
      return;
    }

    fetch(`${BACKEND_URL}/api/bloom/oauth/finish?state=${encodeURIComponent(state)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to finish Instagram connection');
        }
        return r.json();
      })
      .then((data) => {
        setStatus('ok');
        setMessage(`Connected @${data.ig_username} (${data.followers_count?.toLocaleString?.() || 0} followers). Redirecting…`);
        setTimeout(() => navigate('/dashboard?tab=bloom'), 1600);
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-cream-light min-h-screen flex items-center justify-center p-6" data-testid="bloom-callback-page">
      <div className="bg-white rounded-2xl shadow-sm p-10 max-w-md w-full text-center">
        {status === 'working' && <Loader2 className="mx-auto text-navy animate-spin mb-4" size={44} />}
        {status === 'ok' && <CheckCircle2 className="mx-auto text-green-600 mb-4" size={44} />}
        {status === 'error' && <AlertCircle className="mx-auto text-red-500 mb-4" size={44} />}
        <p className="text-navy font-semibold text-lg mb-2" data-testid="bloom-callback-status">
          {status === 'working' && 'Connecting Instagram…'}
          {status === 'ok' && 'Instagram Connected!'}
          {status === 'error' && 'Connection Failed'}
        </p>
        <p className="text-navy/70 text-sm">{message}</p>
        {status === 'error' && (
          <button
            onClick={() => navigate('/dashboard?tab=bloom')}
            className="mt-6 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90"
            data-testid="bloom-callback-back-btn"
          >
            Back to Dashboard
          </button>
        )}
      </div>
    </div>
  );
}
